import React from 'react';
import {
	ApLoaderDefault,
	ApLoaderText,
	ApButtonMain,
	ApButtonGroup,
} from '@alixpartners/ui-components';

import { TableView } from '../TableView';
import { PermissionsContext, PERMISSIONS } from 'contexts/PermissionsContext';
import { UploadStateContext } from 'contexts/UploadStateContext';
import { SettingsContext } from 'contexts/SettingsContext';
import { CellModalEditor } from '../CellModalEditor';
import { ErrorMessageModal } from '../ErrorMessageModal';

import { Wrapper, LoaderContainer, TableContainer } from '../styles';
import {
	BottomSection,
	PublishButton,
	Text,
	ModalBody,
	StyledModal,
	StyledButtonSecondary,
	ModalHeader,
} from './styles';
import { publishData } from 'utils/apiRequests';

export class UnpublishedData extends React.Component {
	state = {
		isTaxonomyTableReady: false,
		isCellModalVisible: false,
		cellModalEditorProps: {},
		isConfirmationOpen: false,
	};

	closeCellModalEditor = () => {
		this.setState({
			isCellModalVisible: false,
			cellModalEditorProps: {},
		});
	};

	openCellModalEditor = cellModalEditorProps => {
		this.setState({
			isCellModalVisible: true,
			cellModalEditorProps,
		});
	};

	setIsTaxonomyTableReady = isReady => {
		this.setState({ isTaxonomyTableReady: isReady });
	};

	showErrorMessage = errorMessageText => {
		this.setState({ isErrorMessageVisible: true, errorMessageText });
	};

	hideErrorMessage = () => {
		this.setState({ isErrorMessageVisible: false, errorMessageText: null });
	};

	publish = () => {
		this.hideConfirmation();

		publishData().then(() => {
			this.props.fetchState();
		});
	};

	showConfirmation = () => {
		this.setState({ isConfirmationOpen: true });
	};

	hideConfirmation = () => {
		this.setState({ isConfirmationOpen: false });
	};

	renderTaxonomyTable() {
		const { isTaxonomyTableReady } = this.state;
		const { permissions, status, customFilters } = this.props;

		return (
			<React.Fragment>
				{!isTaxonomyTableReady ? (
					<LoaderContainer>
						<ApLoaderDefault loaderText={() => <ApLoaderText />} />
					</LoaderContainer>
				) : null}
				<TableContainer isVisible={isTaxonomyTableReady}>
					<TableView
						openCellModalEditor={this.openCellModalEditor}
						setIsReady={this.setIsTaxonomyTableReady}
						showErrorMessage={this.showErrorMessage}
						permissions={permissions || []}
						timestamp={status.lastSuccessClassificationDate}
						customFilterOptions={customFilters}
						isUnpublished
					/>
				</TableContainer>
			</React.Fragment>
		);
	}

	render() {
		const {
			isCellModalVisible,
			cellModalEditorProps,
			isErrorMessageVisible,
			errorMessageText,
			isTaxonomyTableReady,
			isConfirmationOpen,
		} = this.state;
		const { permissions, status } = this.props;

		const canPublish = permissions.includes(PERMISSIONS.UnpublishedDataApprove);

		return (
			<React.Fragment>
				<Wrapper>
					{this.renderTaxonomyTable()}
					{isTaxonomyTableReady &&
						canPublish && (
							<BottomSection>
								<Text>
									This data is not yet visible to the client. Once it’s ready to
									be shown, click Publish.
								</Text>
								<PublishButton onClick={this.showConfirmation}>
									Publish
								</PublishButton>
							</BottomSection>
						)}
					{isCellModalVisible && (
						<CellModalEditor
							onClose={this.closeCellModalEditor}
							{...cellModalEditorProps}
						/>
					)}
					{isErrorMessageVisible && (
						<ErrorMessageModal
							message={errorMessageText}
							onClose={this.hideErrorMessage}
						/>
					)}
				</Wrapper>
				<StyledModal
					isOpen={isConfirmationOpen}
					hasClosed={this.hideConfirmation}
					header={
						<ModalHeader>
							Publish {status.isDataPublished ? 'new' : 'initial'} data
						</ModalHeader>
					}
					footer={({ handleClose }) => {
						return (
							<ApButtonGroup justifyContent="flex-end">
								<StyledButtonSecondary onClick={handleClose}>
									Cancel
								</StyledButtonSecondary>
								<ApButtonMain onClick={this.publish}>Confirm</ApButtonMain>
							</ApButtonGroup>
						);
					}}
				>
					<ModalBody>
						{status.isDataPublished ? (
							'You are about to publish a new data set to the client. Ensure you have made the necessary changes.'
						) : (
							<React.Fragment>
								<p>
									You are about to publish the initial data set to the client.
								</p>
								<p>
									You will now need to configure settings to make the
									application ready for the client to use.
								</p>
							</React.Fragment>
						)}
					</ModalBody>
				</StyledModal>
			</React.Fragment>
		);
	}
}

export default props => (
	<SettingsContext.Consumer>
		{({ customFilters }) => (
			<UploadStateContext.Consumer>
				{({ status, refetch }) => (
					<PermissionsContext.Consumer>
						{({ permissions }) => (
							<UnpublishedData
								{...props}
								status={status}
								fetchState={refetch}
								permissions={permissions}
								customFilters={customFilters}
							/>
						)}
					</PermissionsContext.Consumer>
				)}
			</UploadStateContext.Consumer>
		)}
	</SettingsContext.Consumer>
);
